// src/components/AddCompanyForm/CepRangesCard.jsx
import React, { useMemo, useRef, useState } from "react";
import {
  Card,
  CardHeader,
  CardContent,
  Grid,
  TextField,
  MenuItem,
  InputAdornment,
  Button,
  Typography,
  Box,
  IconButton,
  Switch,
  Alert,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Checkbox,
  TablePagination,
} from "@mui/material";
import LinkRoundedIcon from "@mui/icons-material/LinkRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import UploadFileRoundedIcon from "@mui/icons-material/UploadFileRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import BlockRoundedIcon from "@mui/icons-material/BlockRounded";
import { onlyDigits, validCep8, slugify, uid } from "./utils";

export default function CepRangesCard({ regionKeys = [], cepRanges = [], setCepRanges }) {
  // -------------------- Estados --------------------
  const [newRange, setNewRange] = useState({
    _id: uid(),
    region_key: "",
    start_cep: "",
    end_cep: "",
    target_cod_interno: "",
    status: 1,
  });

  const [feedback, setFeedback] = useState(null); // { type: 'success' | 'error' | 'info' | 'warning', message: string }

  // Dialog de Importação
  const [importOpen, setImportOpen] = useState(false);
  const [importRegion, setImportRegion] = useState("");
  const [importTarget, setImportTarget] = useState("");
  const [importFile, setImportFile] = useState(null);
  const [importBusy, setImportBusy] = useState(false);
  const fileInputRef = useRef(null);

  // Seleção e paginação
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // -------------------- Helpers --------------------
  const clearFeedbackLater = (ms = 6000) => {
    window.clearTimeout(clearFeedbackLater._t);
    clearFeedbackLater._t = window.setTimeout(() => setFeedback(null), ms);
  };

  const norm = (s) =>
    String(s ?? "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .trim();

  const normHeader = (s) => norm(s).replace(/[^a-z0-9]+/g, "_");

  /** Detecta delimitador predominante ("," ou ";") */
  function detectDelimiter(text) {
    const lines = text.split(/\r?\n/).slice(0, 5);
    let commas = 0;
    let semicolons = 0;
    for (const l of lines) {
      commas += (l.match(/,/g) || []).length;
      semicolons += (l.match(/;/g) || []).length;
    }
    return semicolons > commas ? ";" : ",";
  }

  /** Parse CSV (com suporte a aspas) */
  function parseCSV(text, delimiter) {
    const rows = [];
    let i = 0,
      cur = [],
      field = "",
      inQuotes = false;

    const pushField = () => {
      cur.push(field);
      field = "";
    };
    const pushRow = () => {
      rows.push(cur);
      cur = [];
    };

    while (i < text.length) {
      const ch = text[i];

      if (inQuotes) {
        if (ch === '"') {
          if (text[i + 1] === '"') {
            field += '"';
            i += 2;
            continue;
          } else {
            inQuotes = false;
            i++;
            continue;
          }
        } else {
          field += ch;
          i++;
          continue;
        }
      } else {
        if (ch === '"') {
          inQuotes = true;
          i++;
          continue;
        }
        if (ch === delimiter) {
          pushField();
          i++;
          continue;
        }
        if (ch === "\n") {
          pushField();
          pushRow();
          i++;
          continue;
        }
        if (ch === "\r") {
          i++;
          continue;
        }
        field += ch;
        i++;
      }
    }
    // última célula/linha
    pushField();
    if (cur.length > 1 || (cur.length === 1 && cur[0] !== "")) {
      pushRow();
    }
    return rows;
  }

  /** Cabeçalhos aceitos para cep_inicial e cep_final */
  function buildHeaderMap(headers) {
    const H = headers.map((h) => normHeader(h));

    const pick = (cands) => {
      for (const k of cands) {
        const idx = H.indexOf(k);
        if (idx >= 0) return idx;
      }
      return -1;
    };

    const start_cep = pick([
      "cep_inicial",
      "inicio",
      "de",
      "start",
      "inicio_cep",
      "cep_de",
      "inicial",
      "cep_inicio",
      "cep_inicio_de",
    ]);
    const end_cep = pick([
      "cep_final",
      "fim",
      "ate",
      "end",
      "final_cep",
      "cep_ate",
      "final",
      "cep_fim",
      "cep_fim_ate",
    ]);

    return { start_cep, end_cep };
  }

  /** Gera chave para deduplicação */
  const dupeKey = (region_key, start, end, target) =>
    [String(region_key || "").trim(), onlyDigits(start), onlyDigits(end), String(target || "").trim()].join("|");

  // -------------------- Ações (manual) --------------------
  const addRange = () => {
    setFeedback(null);
    const region_key = String(newRange.region_key || "").trim();
    const start_cep = onlyDigits(newRange.start_cep);
    const end_cep = onlyDigits(newRange.end_cep);
    const target = slugify(newRange.target_cod_interno || "");

    if (!region_key || !validCep8(start_cep) || !validCep8(end_cep) || !target) {
      setFeedback({
        type: "error",
        message: "Preencha marca, CEP inicial (8 díg.), CEP final (8 díg.) e destino (slug do projeto).",
      });
      clearFeedbackLater();
      return;
    }
    if (Number(start_cep) > Number(end_cep)) {
      setFeedback({ type: "error", message: "CEP inicial não pode ser maior que o CEP final." });
      clearFeedbackLater();
      return;
    }

    setCepRanges((prev) => [
      ...prev,
      {
        _id: uid(),
        region_key,
        start_cep,
        end_cep,
        target_cod_interno: target,
        status: 1,
        ordem: prev.length,
      },
    ]);

    setNewRange({
      _id: uid(),
      region_key: "",
      start_cep: "",
      end_cep: "",
      target_cod_interno: "",
      status: 1,
    });

    setFeedback({ type: "success", message: "Faixa adicionada." });
    clearFeedbackLater(3000);
  };

  const removeRange = (id) => setCepRanges((p) => p.filter((r) => r._id !== id));

  const toggleRangeStatus = (id) =>
    setCepRanges((prev) => prev.map((r) => (r._id === id ? { ...r, status: r.status === 1 ? 0 : 1 } : r)));

  const onRangeKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addRange();
    }
  };

  // -------------------- Importação (Dialog) --------------------
  const openImport = () => {
    setFeedback(null);
    setImportRegion("");
    setImportTarget("");
    setImportFile(null);
    setImportOpen(true);
  };
  const closeImport = () => {
    if (!importBusy) setImportOpen(false);
  };

  const handlePickFile = () => fileInputRef.current?.click();
  const onFileChange = (e) => {
    const f = e.target.files?.[0] || null;
    e.target.value = "";
    setImportFile(f);
  };

  const handleDownloadTemplate = () => {
    const headers = ["CEP Inicial", "CEP Final"];
    const sample = [
      ["88000000", "88009999"],
      ["88010000", "88019999"],
    ];
    const csv = [headers.join(";"), ...sample.map((r) => r.join(";"))].join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "exemplo-faixas-cep.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const runImport = async () => {
    try {
      setFeedback(null);

      const region_key = String(importRegion || "").trim();
      const target = slugify(importTarget || "");
      if (!region_key) {
        setFeedback({ type: "error", message: "Selecione a marca para a importação." });
        clearFeedbackLater();
        return;
      }
      if (!target) {
        setFeedback({ type: "error", message: "Informe o destino (slug do projeto) para a importação." });
        clearFeedbackLater();
        return;
      }
      if (!importFile) {
        setFeedback({ type: "error", message: "Selecione um arquivo CSV para importar." });
        clearFeedbackLater();
        return;
      }

      setImportBusy(true);
      const text = await importFile.text();
      const delimiter = detectDelimiter(text);
      const rows = parseCSV(text, delimiter);

      if (!rows.length) {
        setFeedback({ type: "error", message: "Arquivo vazio." });
        clearFeedbackLater();
        setImportBusy(false);
        return;
      }

      const headers = rows[0];
      const dataRows = rows.slice(1).filter((r) => r.some((c) => String(c || "").trim() !== ""));
      if (!dataRows.length) {
        setFeedback({ type: "error", message: "Nenhum dado encontrado após o cabeçalho." });
        clearFeedbackLater();
        setImportBusy(false);
        return;
      }

      const hmap = buildHeaderMap(headers);
      if (hmap.start_cep < 0 || hmap.end_cep < 0) {
        setFeedback({
          type: "error",
          message:
            "Não encontrei colunas de CEP (inicial/final). Use cabeçalhos como 'CEP Inicial' e 'CEP Final' (variações aceitas).",
        });
        clearFeedbackLater();
        setImportBusy(false);
        return;
      }

      const next = [];
      const errors = [];
      const seen = new Set();

      const existing = new Set(
        (cepRanges || []).map((r) => dupeKey(r.region_key, r.start_cep, r.end_cep, r.target_cod_interno))
      );

      for (let i = 0; i < dataRows.length; i++) {
        const row = dataRows[i];
        const rowNum = i + 2;

        const start_cep = onlyDigits(row[hmap.start_cep] || "");
        const end_cep = onlyDigits(row[hmap.end_cep] || "");

        if (!validCep8(start_cep)) {
          errors.push(`L${rowNum}: CEP inicial inválido (${row[hmap.start_cep] ?? ""}).`);
          continue;
        }
        if (!validCep8(end_cep)) {
          errors.push(`L${rowNum}: CEP final inválido (${row[hmap.end_cep] ?? ""}).`);
          continue;
        }
        if (Number(start_cep) > Number(end_cep)) {
          errors.push(`L${rowNum}: CEP inicial maior que o final (${start_cep} > ${end_cep}).`);
          continue;
        }

        const key = dupeKey(region_key, start_cep, end_cep, target);
        if (seen.has(key) || existing.has(key)) continue;

        seen.add(key);
        next.push({
          _id: uid(),
          region_key,
          start_cep,
          end_cep,
          target_cod_interno: target,
          status: 1,
        });
      }

      if (!next.length && !errors.length) {
        setFeedback({ type: "info", message: "Nada novo para importar (tudo já existente ou inválido)." });
        clearFeedbackLater(5000);
        setImportBusy(false);
        return;
      }

      setCepRanges((prev) => {
        const base = prev.length;
        return [...prev, ...next.map((r, idx) => ({ ...r, ordem: base + idx }))];
      });

      const okMsg = `Importação concluída: ${next.length} linha(s) adicionada(s)${
        errors.length ? `, ${errors.length} com problema(s)` : ""
      }.`;
      setFeedback({
        type: errors.length ? "warning" : "success",
        message:
          okMsg +
          (errors.length ? ` Exemplos: ${errors.slice(0, 3).join(" | ")}${errors.length > 3 ? " ..." : ""}` : ""),
      });
      clearFeedbackLater(errors.length ? 10000 : 5000);

      setImportBusy(false);
    } catch (err) {
      setImportBusy(false);
      setFeedback({
        type: "error",
        message: `Falha ao importar: ${err?.message || "erro inesperado"}.`,
      });
      clearFeedbackLater(8000);
    }
  };

  // -------------------- Paginação e seleção --------------------
  const total = cepRanges.length;
  const pageCount = Math.ceil(total / rowsPerPage) || 1;
  const safePage = Math.min(page, pageCount - 1);
  const start = safePage * rowsPerPage;
  const end = Math.min(start + rowsPerPage, total);
  const pageRows = cepRanges.slice(start, end);

  const isSelected = (id) => selectedIds.has(id);
  const toggleRowSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectCurrentPage = (checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const r of pageRows) {
        if (checked) next.add(r._id);
        else next.delete(r._id);
      }
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  // -------------------- Ações em massa (botões diretos) --------------------
  const bulkActivate = () => {
    if (selectedIds.size === 0) return;
    setCepRanges((prev) => prev.map((r) => (selectedIds.has(r._id) ? { ...r, status: 1 } : r)));
  };
  const bulkDeactivate = () => {
    if (selectedIds.size === 0) return;
    setCepRanges((prev) => prev.map((r) => (selectedIds.has(r._id) ? { ...r, status: 0 } : r)));
  };
  const bulkDelete = () => {
    if (selectedIds.size === 0) return;
    const toRemove = selectedIds.size;
    setCepRanges((prev) => prev.filter((r) => !selectedIds.has(r._id)));
    setSelectedIds(new Set());
    // reajusta página se esvaziar
    const newTotal = total - toRemove;
    const newPageCount = Math.ceil(Math.max(newTotal, 0) / rowsPerPage) || 1;
    if (page >= newPageCount) setPage(Math.max(0, newPageCount - 1));
  };

  // -------------------- Render --------------------
  const headerSub = useMemo(() => "", []);

  const allPageSelected = pageRows.length > 0 && pageRows.every((r) => selectedIds.has(r._id));
  const somePageSelected = pageRows.some((r) => selectedIds.has(r._id));

  // Feedback global (fora do modal) só quando o modal estiver fechado
  const renderGlobalFeedback = !importOpen && feedback ? (
    <Alert severity={feedback.type} sx={{ mb: 2 }} onClose={() => setFeedback(null)}>
      {feedback.message}
    </Alert>
  ) : null;

  return (
    <Card variant="outlined">
      <CardHeader
        title="Faixas de CEP"
        subheader={headerSub}
        action={
          <Stack direction="row" spacing={1}>
            {/* Removido Exemplo CSV fora do modal */}
            <Button variant="contained" startIcon={<UploadFileRoundedIcon />} onClick={openImport}>
              Importar
            </Button>
          </Stack>
        }
      />
      <CardContent sx={{ pt: 0 }}>
        {renderGlobalFeedback}

        {/* Linha de criação manual */}
        <Grid container spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <Grid item xs={12} md={3}>
            <TextField
              label="Marca"
              select
              fullWidth
              value={newRange.region_key}
              onChange={(e) => setNewRange((p) => ({ ...p, region_key: e.target.value }))}
              onKeyDown={onRangeKeyDown}
              placeholder="Selecione uma marca"
            >
              {regionKeys.map((k) => (
                <MenuItem key={k} value={k}>
                  {k}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              label="CEP inicial"
              value={newRange.start_cep}
              onChange={(e) => setNewRange((p) => ({ ...p, start_cep: onlyDigits(e.target.value) }))}
              onKeyDown={onRangeKeyDown}
              fullWidth
              inputProps={{ maxLength: 8 }}
              placeholder="01001000"
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              label="CEP final"
              value={newRange.end_cep}
              onChange={(e) => setNewRange((p) => ({ ...p, end_cep: onlyDigits(e.target.value) }))}
              onKeyDown={onRangeKeyDown}
              fullWidth
              inputProps={{ maxLength: 8 }}
              placeholder="01500999"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              label="Destino (slug do projeto)"
              value={newRange.target_cod_interno}
              onChange={(e) => setNewRange((p) => ({ ...p, target_cod_interno: e.target.value }))}
              onKeyDown={onRangeKeyDown}
              fullWidth
              placeholder="ex.: filial"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LinkRoundedIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Button variant="outlined" onClick={addRange} fullWidth>
              Adicionar
            </Button>
          </Grid>
        </Grid>

        {/* Toolbar de seleção + botões de ação (só aparece com seleção) */}
        {selectedIds.size !== 0 && (
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            alignItems={{ xs: "stretch", sm: "center" }}
            sx={{ mb: 1 }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              {/* <Checkbox
                indeterminate={!allPageSelected && somePageSelected}
                checked={allPageSelected}
                onChange={(e) => selectCurrentPage(e.target.checked)}
                inputProps={{ "aria-label": "Selecionar página" }}
              /> */}
              <Typography variant="body2">
                {`${selectedIds.size} registro(s) selecionado(s)`}
              </Typography>
            </Stack>

            <Button
              variant="contained"
              startIcon={<CheckCircleRoundedIcon />}
              onClick={bulkActivate}
            >
              Ativar selecionados ({selectedIds.size})
            </Button>

            <Button
              variant="contained"
              color="warning"
              startIcon={<BlockRoundedIcon />}
              onClick={bulkDeactivate}
            >
              Desativar selecionados ({selectedIds.size})
            </Button>

            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteOutlineRoundedIcon />}
              onClick={bulkDelete}
            >
              Excluir selecionados ({selectedIds.size})
            </Button>

            <Button variant="text" onClick={clearSelection}>
              Limpar seleção
            </Button>

            <Box flex={1} />
          </Stack>
        )}

        {/* Tabela de faixas + paginação */}
        {cepRanges.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Nenhuma faixa cadastrada.
          </Typography>
        ) : (
          <>
            <Box sx={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    {["", "Marca", "CEP inicial", "CEP final", "Destino (slug do projeto)", "Ativo", "Excluir"].map((h) => (
                      <th
                        key={h || "sel"}
                        style={{
                          borderBottom: "1px solid #e0e0e0",
                          padding: 8,
                          textAlign: "left",
                          fontWeight: 600,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {h === "" ? (
                          <Checkbox
                            indeterminate={!allPageSelected && somePageSelected}
                            checked={allPageSelected}
                            onChange={(e) => selectCurrentPage(e.target.checked)}
                            inputProps={{ "aria-label": "Selecionar página" }}
                          />
                        ) : (
                          h
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map((r) => (
                    <tr key={r._id}>
                      <td style={{ padding: 8 }}>
                        <Checkbox
                          checked={isSelected(r._id)}
                          onChange={() => toggleRowSelect(r._id)}
                          inputProps={{ "aria-label": "Selecionar linha" }}
                        />
                      </td>
                      <td style={{ padding: 8 }}>{r.region_key}</td>
                      <td style={{ padding: 8 }}>{onlyDigits(r.start_cep)}</td>
                      <td style={{ padding: 8 }}>{onlyDigits(r.end_cep)}</td>
                      <td style={{ padding: 8 }}>{r.target_cod_interno}</td>
                      <td style={{ padding: 8 }}>
                        <Switch
                          size="small"
                          checked={Number(r.status) === 1}
                          onChange={() => toggleRangeStatus(r._id)}
                        />
                      </td>
                      <td style={{ padding: 8 }}>
                        <IconButton size="small" onClick={() => removeRange(r._id)}>
                          <DeleteOutlineRoundedIcon fontSize="small" />
                        </IconButton>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>

            <TablePagination
              component="div"
              count={total}
              page={safePage}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25, 50, 100]}
              labelRowsPerPage="Linhas por página"
            />
          </>
        )}
      </CardContent>

      {/* Dialog de Importação */}
      <Dialog open={importOpen} onClose={closeImport} fullWidth maxWidth="sm">
        <DialogTitle>Importar faixas de CEP</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {/* Feedback dentro do modal */}
            {feedback && (
              <Alert severity={feedback.type} onClose={() => setFeedback(null)}>
                {feedback.message}
              </Alert>
            )}

            <Typography variant="body2" color="text.secondary">
              O arquivo deve conter cabeçalhos reconhecíveis para <strong>CEP Inicial</strong> e{" "}
              <strong>CEP Final</strong>. Variações comuns são aceitas (ex.: “cep_inicial”, “início”, “de” / “cep_final”,
              “fim”, “até”).
            </Typography>

            <TextField
              label="Marca"
              select
              fullWidth
              value={importRegion}
              onChange={(e) => setImportRegion(e.target.value)}
              disabled={importBusy}
            >
              {regionKeys.map((k) => (
                <MenuItem key={k} value={k}>
                  {k}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Destino (slug do projeto)"
              value={importTarget}
              onChange={(e) => setImportTarget(e.target.value)}
              placeholder="ex.: filial"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LinkRoundedIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              disabled={importBusy}
              fullWidth
            />

            <Stack direction="row" spacing={1} alignItems="center">
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                style={{ display: "none" }}
                onChange={onFileChange}
              />
              <Button
                variant="outlined"
                onClick={handlePickFile}
                disabled={importBusy}
                startIcon={<UploadFileRoundedIcon />}
              >
                Selecionar CSV
              </Button>
              <Typography variant="body2" color="text.secondary" noWrap title={importFile?.name || ""}>
                {importFile?.name || "Nenhum arquivo selecionado"}
              </Typography>
              <Box flex={1} />
              <Button
                variant="text"
                onClick={handleDownloadTemplate}
                disabled={importBusy}
                startIcon={<DownloadRoundedIcon />}
              >
                Baixar exemplo
              </Button>
            </Stack>

            {importBusy && <LinearProgress />}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeImport} disabled={importBusy}>
            Fechar
          </Button>
          <Button onClick={runImport} variant="contained" disabled={importBusy}>
            Importar
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
